/**
 * ч.1084 (рішення власника): «Крок 1. Довідники з файлу» сторінки «Початкові
 * дані (без 1С)» — колишня окрема сторінка /import-csv.
 *
 * Для переходу НЕ з 1С (з 1С довідники переносить обробка майстра міграції):
 *   - файл Excel (.xlsx — розбирає backend `/import/parse-file`) або CSV
 *     (UTF-8 чи Windows-1251 — визначаємо самі);
 *   - колонки привʼязуються автоматично за українськими назвами (шаблон
 *     кожного довідника можна завантажити);
 *   - рядки перевіряються ДО відправки; сервер пише атомарно (помилка → 0).
 * Співробітники (ч.1084): фізособа + співробітник + стартовий кадровий стан
 * (оклад); посада і підрозділ — текстом, відсутні створюються.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  imports,
  parseImportFile,
  NEEDS_ORG_SELECTOR,
  type ImportKind,
  type ContractorImportRow,
  type NomenclatureImportRow,
  type DepartmentImportRow,
  type PositionImportRow,
  type EmployeeImportRow,
  type ImportResult,
} from '@/api/openingBalances';
import { orgs } from '@/api/catalogs';
import { humanizeBackendError } from '@/utils/backendError';
import { pickDefaultOrg } from '@/store/defaults';
import TchTable from '@/shared/components/TchTable';

type Kind = ImportKind;

/** Джерело для повторних завантажень (ключ ID з файлу). Для 1С — обробка. */
const SOURCE = 'EXCEL';

export function parseCsv(text: string): string[][] {
  const out: string[][] = [];
  const lines = text.replace(/^﻿/, '').split(/\r?\n/);
  const firstLine = lines[0] ?? '';
  const semi = (firstLine.match(/;/g) ?? []).length;
  const comma = (firstLine.match(/,/g) ?? []).length;
  const tab = (firstLine.match(/\t/g) ?? []).length;
  const sep = tab > semi && tab > comma ? '\t' : semi >= comma && semi > 0 ? ';' : ',';
  for (const line of lines) {
    if (line.trim() === '') continue;
    const row: string[] = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i]!;
      if (inQuotes) {
        if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
        else if (ch === '"') { inQuotes = false; }
        else { cur += ch; }
      } else if (ch === '"') { inQuotes = true; }
      else if (ch === sep) { row.push(cur.trim()); cur = ''; }
      else { cur += ch; }
    }
    row.push(cur.trim());
    out.push(row);
  }
  return out;
}

/** Байти файлу → текст: UTF-8, а якщо файл не UTF-8 — Windows-1251 (Excel). */
export function decodeCsv(buf: ArrayBuffer): string {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(buf);
  } catch {
    return new TextDecoder('windows-1251').decode(buf);
  }
}

/** Байти файлу: File.arrayBuffer, а де його немає — FileReader. */
function readFileBuffer(file: File): Promise<ArrayBuffer> {
  if (typeof file.arrayBuffer === 'function') return file.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error ?? new Error('Не вдалося прочитати файл'));
    reader.readAsArrayBuffer(file);
  });
}

interface ColDef {
  key: string;
  label: string;
  required?: boolean;
  /** Інші назви колонки у файлі (нижній регістр) — для автопривʼязки. */
  aliases?: string[];
  /** Приклад значення для шаблону. */
  example?: string;
}

const ID_COL: ColDef = {
  key: 'external_id', label: 'ID (необовʼязково)', aliases: ['id', 'ід', 'external_id'],
};

export const COLS: Record<Kind, ColDef[]> = {
  contractors: [
    { key: 'name', label: 'Назва', required: true,
      aliases: ['найменування', 'контрагент', 'name'], example: 'ТОВ «Приклад»' },
    { key: 'full_name', label: 'Повна назва', aliases: ['повне найменування', 'full_name'],
      example: 'Товариство з обмеженою відповідальністю «Приклад»' },
    { key: 'contractor_type', label: 'Тип (юрособа / ФОП / фізособа)',
      aliases: ['тип', 'вид', 'тип контрагента'], example: 'юрособа' },
    { key: 'edrpou', label: 'ЄДРПОУ', aliases: ['едрпоу', 'код єдрпоу', 'код за єдрпоу', 'edrpou'],
      example: '12345678' },
    { key: 'inn', label: 'РНОКПП (ФОП, фізособи)', aliases: ['рнокпп', 'інн', 'inn'] },
    { key: 'is_vat_payer', label: 'Платник ПДВ (так / ні)', aliases: ['платник пдв', 'пдв'],
      example: 'так' },
    { key: 'vat_number', label: 'ІПН платника ПДВ',
      aliases: ['іпн', 'номер платника пдв', 'податковий номер', 'vat_number'],
      example: '123456789012' },
    { key: 'code', label: 'Код', aliases: ['code'] },
    ID_COL,
  ],
  nomenclature: [
    { key: 'name', label: 'Назва', required: true,
      aliases: ['найменування', 'номенклатура', 'товар', 'name'], example: 'Папір А4' },
    { key: 'item_type', label: 'Тип (товар / послуга)', aliases: ['тип', 'вид'],
      example: 'товар' },
    { key: 'article', label: 'Артикул', aliases: ['article'], example: 'A4-500' },
    { key: 'full_name', label: 'Повна назва', aliases: ['повне найменування', 'full_name'] },
    { key: 'code', label: 'Код', aliases: ['code'] },
    ID_COL,
  ],
  employees: [
    { key: 'last_name', label: 'Прізвище', required: true, aliases: ['фамилия'],
      example: 'Коваль' },
    { key: 'first_name', label: 'Імʼя', required: true,
      aliases: ["ім'я", 'імя', 'имя'], example: 'Ірина' },
    { key: 'middle_name', label: 'По батькові', aliases: ['отчество'], example: 'Петрівна' },
    { key: 'inn', label: 'РНОКПП', aliases: ['іпн', 'інн', 'inn'], example: '1234567890' },
    { key: 'code', label: 'Табельний номер', aliases: ['табельний', 'таб. №', 'код'],
      example: '0001' },
    { key: 'position', label: 'Посада', aliases: ['должность'], example: 'Бухгалтер' },
    { key: 'department', label: 'Підрозділ', aliases: ['відділ', 'подразделение'],
      example: 'Бухгалтерія' },
    { key: 'hire_date', label: 'Дата прийому',
      aliases: ['дата прийняття', 'дата прийому на роботу', 'дата приема'],
      example: '10.01.2026' },
    { key: 'salary', label: 'Оклад', aliases: ['оклад, грн', 'ставка'], example: '20000' },
    { key: 'employment_type', label: 'Вид (штатний / сумісник / ЦПХ)',
      aliases: ['вид зайнятості', 'вид'], example: 'штатний' },
    ID_COL,
  ],
  departments: [
    { key: 'code', label: 'Код', required: true, aliases: ['code'], example: '01' },
    { key: 'name', label: 'Назва', required: true, aliases: ['найменування', 'підрозділ'],
      example: 'Бухгалтерія' },
    ID_COL,
  ],
  positions: [
    { key: 'code', label: 'Код', required: true, aliases: ['code'], example: '01' },
    { key: 'name', label: 'Назва', required: true, aliases: ['найменування', 'посада'],
      example: 'Бухгалтер' },
    { key: 'kp_code', label: 'Код КП', aliases: ['кп', 'код класифікатора професій'],
      example: '3433' },
    ID_COL,
  ],
};

export const KIND_LABELS: Record<Kind, string> = {
  contractors: 'Контрагенти',
  nomenclature: 'Номенклатура',
  employees: 'Співробітники',
  departments: 'Підрозділи',
  positions: 'Посади',
};
const KIND_ORDER: Kind[] = ['contractors', 'nomenclature', 'employees', 'departments', 'positions'];

const KIND_NOTE: Record<Kind, string> = {
  contractors: 'Тип визначиться сам, якщо колонки немає: є ЄДРПОУ — юрособа, лише РНОКПП — ФОП.',
  nomenclature: 'Без колонки «Тип» усе завантажиться як товари.',
  employees: 'Разом із працівником створюється його кадровий стан на дату прийому з окладом — '
    + 'зарплата рахується одразу. Посади й підрозділи, яких ще немає, створяться самі. '
    + 'Повторне завантаження дублів не дає: знайдені працівники (за ID, табельним номером або '
    + 'РНОКПП) лише доповнюються.',
  departments: 'Підрозділи належать організації — оберіть її вище.',
  positions: 'Посади спільні для всіх організацій акаунта.',
};

const norm = (s: string) => s.toLowerCase().replace(/[ʼ’`]/g, "'").replace(/\*/g, '')
  .replace(/\s+/g, ' ').trim();

/** Автопривʼязка: точна назва (підпис або синонім), потім «починається з». */
export function autoMap(kind: Kind, headers: string[]): Record<string, number> {
  const hs = headers.map(norm);
  const used = new Set<number>();
  const result: Record<string, number> = {};
  const names = (c: ColDef) => [norm(c.label), norm(c.label.split(' (')[0]!), ...(c.aliases ?? []).map(norm)];
  for (const pass of ['exact', 'prefix'] as const) {
    for (const col of COLS[kind]) {
      if (result[col.key] !== undefined) continue;
      const idx = hs.findIndex((h, i) => !used.has(i) && h !== '' && names(col).some(
        (n) => (pass === 'exact' ? h === n
          : h.length >= 3 && n.length >= 3 && (h.startsWith(n) || n.startsWith(h)))));
      if (idx >= 0) { result[col.key] = idx; used.add(idx); }
    }
  }
  return result;
}

const YES = new Set(['так', 'да', '1', 'true', '+', 'yes', 'є']);
const NO = new Set(['ні', 'нет', '0', 'false', '-', 'no', '']);

/** Excel зʼїдає провідні нулі у числах: ЄДРПОУ 5–7 цифр → 8, РНОКПП 8–9 → 10. */
export function padDigits(v: string | undefined, len: number, minLen: number): string | undefined {
  if (v === undefined) return v;
  const s = v.replace(/\s/g, '').replace(/\.0$/, '');
  return /^\d+$/.test(s) && s.length >= minLen && s.length < len ? s.padStart(len, '0') : s;
}

function contractorType(raw: string | undefined, edrpou?: string, inn?: string):
  ContractorImportRow['contractor_type'] | null {
  const v = norm(raw ?? '');
  if (v === '') return edrpou ? 'LEGAL' : inn ? 'INDIVIDUAL_FOP' : 'LEGAL';
  if (['юрособа', 'юридична особа', 'юр. особа', 'legal'].includes(v)) return 'LEGAL';
  if (['фоп', 'фізична особа-підприємець', 'фізична особа - підприємець', 'individual_fop']
    .includes(v)) return 'INDIVIDUAL_FOP';
  if (['фізособа', 'фізична особа', 'individual'].includes(v)) return 'INDIVIDUAL';
  if (['відокремлений підрозділ', 'separate_division'].includes(v)) return 'SEPARATE_DIVISION';
  return null;
}

function itemType(raw: string | undefined): NomenclatureImportRow['item_type'] | null {
  const v = norm(raw ?? '');
  if (v === '' || v === 'товар' || v === 'good') return 'GOOD';
  if (v === 'послуга' || v === 'service') return 'SERVICE';
  return null;
}

/** Рядки файлу → рядки запиту; помилки — з номером рядка (як у файлі-даних). */
export function buildRows(
  kind: Kind, rows: string[][], mapping: Record<string, number>,
): { payload: unknown[]; errors: string[] } {
  const errors: string[] = [];
  const cell = (row: string[], key: string): string | undefined => {
    const idx = mapping[key];
    if (idx === undefined || idx < 0) return undefined;
    const v = (row[idx] ?? '').trim();
    return v === '' ? undefined : v;
  };
  const payload = rows.map((row, i) => {
    const line = i + 1;
    for (const col of COLS[kind]) {
      if (col.required && !cell(row, col.key)) {
        errors.push(`Рядок ${line}: не заповнено «${col.label}»`);
      }
    }
    if (kind === 'contractors') {
      const edrpou = padDigits(cell(row, 'edrpou'), 8, 5);
      const inn = padDigits(cell(row, 'inn'), 10, 8);
      const type = contractorType(cell(row, 'contractor_type'), edrpou, inn);
      if (type === null) {
        errors.push(`Рядок ${line}: незрозумілий тип «${cell(row, 'contractor_type')}» `
          + '— вкажіть «юрособа», «ФОП» або «фізособа»');
      }
      const vatRaw = norm(cell(row, 'is_vat_payer') ?? '');
      if (!YES.has(vatRaw) && !NO.has(vatRaw)) {
        errors.push(`Рядок ${line}: «Платник ПДВ» — так або ні`);
      }
      return {
        external_id: cell(row, 'external_id') ?? null,
        code: cell(row, 'code') ?? null,
        name: cell(row, 'name') ?? '',
        full_name: cell(row, 'full_name') ?? null,
        contractor_type: type ?? 'LEGAL',
        edrpou: edrpou ?? null,
        inn: inn ?? null,
        is_vat_payer: YES.has(vatRaw),
        vat_number: cell(row, 'vat_number') ?? null,
        is_buyer: true, is_supplier: true, is_other: false,
      } satisfies ContractorImportRow;
    }
    if (kind === 'nomenclature') {
      const t = itemType(cell(row, 'item_type'));
      if (t === null) {
        errors.push(`Рядок ${line}: незрозумілий тип «${cell(row, 'item_type')}» — товар або послуга`);
      }
      return {
        external_id: cell(row, 'external_id') ?? null,
        code: cell(row, 'code') ?? null,
        article: cell(row, 'article') ?? null,
        name: cell(row, 'name') ?? '',
        full_name: cell(row, 'full_name') ?? null,
        item_type: t ?? 'GOOD',
      } satisfies NomenclatureImportRow;
    }
    if (kind === 'departments') {
      return {
        external_id: cell(row, 'external_id') ?? null,
        code: cell(row, 'code') ?? '',
        name: cell(row, 'name') ?? '',
      } satisfies DepartmentImportRow;
    }
    if (kind === 'positions') {
      return {
        external_id: cell(row, 'external_id') ?? null,
        code: cell(row, 'code') ?? '',
        name: cell(row, 'name') ?? '',
        kp_code: cell(row, 'kp_code') ?? null,
      } satisfies PositionImportRow;
    }
    // employees: дати/суми/РНОКПП/вид перевіряє сервер (атомарно, з рядками).
    return {
      external_id: cell(row, 'external_id') ?? null,
      code: cell(row, 'code') ?? null,
      last_name: cell(row, 'last_name') ?? '',
      first_name: cell(row, 'first_name') ?? '',
      middle_name: cell(row, 'middle_name') ?? null,
      inn: cell(row, 'inn') ?? null,
      position: cell(row, 'position') ?? null,
      department: cell(row, 'department') ?? null,
      hire_date: cell(row, 'hire_date') ?? null,
      salary: cell(row, 'salary') ?? null,
      employment_type: cell(row, 'employment_type') ?? null,
    } satisfies EmployeeImportRow;
  });
  return { payload, errors };
}

/** Шаблон довідника: CSV з BOM (Excel відкриває кирилицю правильно). */
function downloadTemplate(kind: Kind) {
  const cols = COLS[kind];
  const csv = '﻿' + [cols.map((c) => c.label), cols.map((c) => c.example ?? '')]
    .map((r) => r.map((v) => (/[;"\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v)).join(';'))
    .join('\r\n') + '\r\n';
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `шаблон — ${KIND_LABELS[kind].toLowerCase()}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function needsOrg(k: Kind): boolean {
  return (NEEDS_ORG_SELECTOR as readonly string[]).includes(k);
}

/** 422 сервера → перелік рядків (імпорт співробітників) або одне повідомлення. */
function serverErrors(e: unknown): string[] {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (detail && typeof detail === 'object' && Array.isArray((detail as { errors?: unknown }).errors)) {
    return [...(detail as { errors: string[] }).errors,
      'Нічого не збережено — виправте рядки у файлі і завантажте його знову.'];
  }
  return [humanizeBackendError(e)];
}

export default function RefsFileImport() {
  const qc = useQueryClient();
  const [kind, setKind] = useState<Kind>('contractors');
  const [orgId, setOrgId] = useState('');
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<Record<string, number>>({});
  const [result, setResult] = useState<ImportResult | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [reading, setReading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  useEffect(() => {
    if (!orgId && orgList.data?.items[0]) setOrgId(pickDefaultOrg(orgList.data.items)!.id);
  }, [orgList.data, orgId]);

  const cols = COLS[kind];

  function load(hdr: string[], data: string[][], k: Kind = kind) {
    setHeaders(hdr);
    setRows(data);
    setMapping(autoMap(k, hdr));
    setResult(null);
    setErrors(data.length === 0 ? ['У файлі немає рядків з даними під шапкою'] : []);
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setFileName(file.name);
    setErrors([]);
    setResult(null);
    setReading(true);
    try {
      if (/\.xlsx$/i.test(file.name)) {
        const parsed = await parseImportFile(file);
        load(parsed.headers, parsed.rows);
      } else if (/\.xls$/i.test(file.name)) {
        setErrors(['Формат .xls не підтримується — збережіть файл як .xlsx']);
      } else {
        const parsed = parseCsv(decodeCsv(await readFileBuffer(file)));
        if (parsed.length === 0) { setErrors(['Порожній файл']); return; }
        load(parsed[0]!, parsed.slice(1));
      }
    } catch (err) {
      setErrors([humanizeBackendError(err)]);
    } finally {
      setReading(false);
    }
  }

  function onKindChange(k: Kind) {
    setKind(k);
    setMapping(autoMap(k, headers));
    setResult(null);
    setErrors([]);
  }

  const built = useMemo(() => buildRows(kind, rows, mapping), [kind, rows, mapping]);
  const previewRows = rows.slice(0, 10);
  const withIdCount = useMemo(() => {
    const idx = mapping['external_id'];
    if (idx === undefined || idx < 0) return 0;
    return rows.filter((r) => (r[idx] ?? '').trim() !== '').length;
  }, [rows, mapping]);

  function validate(): boolean {
    setResult(null);
    const missing = cols.filter((c) => c.required
      && (mapping[c.key] === undefined || mapping[c.key]! < 0));
    if (missing.length) {
      setErrors(missing.map((c) => `Не вибрано колонку для «${c.label}»`));
      return false;
    }
    if (needsOrg(kind) && !orgId) {
      setErrors(['Оберіть організацію']);
      return false;
    }
    if (built.errors.length) {
      setErrors(built.errors.slice(0, 50).concat(built.errors.length > 50
        ? [`…і ще ${built.errors.length - 50} помилок`] : []));
      return false;
    }
    setErrors([]);
    return true;
  }

  async function runImport() {
    const effectiveOrg = orgId || (pickDefaultOrg(orgList.data?.items)?.id ?? '');
    if (!effectiveOrg) { setErrors(['Немає доступної організації']); return; }
    setLoading(true);
    try {
      const p = built.payload;
      let res: ImportResult;
      if (kind === 'contractors') res = await imports.contractors(effectiveOrg, SOURCE, p as ContractorImportRow[]);
      else if (kind === 'nomenclature') res = await imports.nomenclature(effectiveOrg, SOURCE, p as NomenclatureImportRow[]);
      else if (kind === 'departments') res = await imports.departments(effectiveOrg, SOURCE, p as DepartmentImportRow[]);
      else if (kind === 'positions') res = await imports.positions(effectiveOrg, SOURCE, p as PositionImportRow[]);
      else res = await imports.employees(effectiveOrg, SOURCE, p as EmployeeImportRow[]);
      setResult(res);
      void qc.invalidateQueries();
    } catch (e) {
      setErrors(serverErrors(e));
    } finally {
      setLoading(false);
      setConfirmOpen(false);
    }
  }

  return (
    <div style={{ paddingBottom: '0.75rem' }}>
      <div className="seg-tabs" style={{ marginBottom: '0.75rem' }}>
        {KIND_ORDER.map((k) => (
          <button key={k} type="button" className={kind === k ? 'active' : ''}
            onClick={() => onKindChange(k)}>
            {KIND_LABELS[k]}
          </button>
        ))}
      </div>

      <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: '0 0 0.75rem' }}>
        Таблиця Excel (.xlsx) або CSV: перший рядок — назви колонок, далі — дані.{' '}
        {KIND_NOTE[kind]}{' '}
        <button type="button" className="btn-link" onClick={() => downloadTemplate(kind)}>
          завантажити шаблон
        </button>
      </p>

      <div style={{
        display: 'grid', gap: '1rem', marginBottom: '0.75rem',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
      }}>
        {needsOrg(kind) && (
          <div className="field" style={{ margin: 0 }}>
            <label htmlFor="refs-import-org">Організація</label>
            <select id="refs-import-org" value={orgId} onChange={(e) => setOrgId(e.target.value)}>
              <option value="">—</option>
              {orgList.data?.items.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>
        )}
        <div className="field" style={{ margin: 0 }}>
          <label htmlFor="refs-import-file">Файл (.xlsx або .csv)</label>
          <input id="refs-import-file" type="file" accept=".xlsx,.csv,.txt" onChange={onFile} />
          {(fileName || reading) && (
            <small style={{ color: 'var(--text-secondary)' }}>
              {reading ? 'Читаємо файл…' : `${fileName}: ${rows.length} рядків`}
            </small>
          )}
        </div>
      </div>

      {headers.length > 0 && (
        <>
          <h4 style={{ margin: '0.75rem 0 0.25rem' }}>Привʼязка колонок</h4>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: '0 0 0.5rem' }}>
            Колонки файлу підібрано за назвами — перевірте. Обовʼязкові позначено *.
          </p>
          <div style={{
            display: 'grid', gap: '0.5rem',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          }}>
            {cols.map((col) => (
              <div key={col.key} className="field" style={{ marginBottom: 0 }}>
                <label htmlFor={`map-${col.key}`} style={{ fontSize: '0.8rem' }}>
                  {col.label}
                  {col.required && <span style={{ color: 'var(--danger)' }}> *</span>}
                </label>
                <select id={`map-${col.key}`} value={mapping[col.key] ?? ''}
                  onChange={(e) => setMapping({
                    ...mapping, [col.key]: e.target.value === '' ? -1 : Number(e.target.value),
                  })}>
                  <option value="">— не завантажувати —</option>
                  {headers.map((h, i) => (
                    <option key={i} value={i}>{h || `Колонка ${i + 1}`}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          <h4 style={{ margin: '0.75rem 0 0.25rem' }}>Перші рядки файлу</h4>
          <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
            <TchTable>
              <thead>
                <tr>{headers.map((h, i) => <th key={i}>{h || `Кол. ${i + 1}`}</th>)}</tr>
              </thead>
              <tbody>
                {previewRows.map((row, i) => (
                  <tr key={i}>{headers.map((_, j) => <td key={j}>{row[j] ?? ''}</td>)}</tr>
                ))}
              </tbody>
            </TchTable>
          </div>

          <div style={{ marginTop: '0.75rem' }}>
            <button type="button" className="primary" disabled={loading || rows.length === 0}
              onClick={() => { if (validate()) setConfirmOpen(true); }}>
              {loading ? 'завантажуємо…' : `завантажити ${rows.length} рядків`}
            </button>
          </div>
        </>
      )}

      {confirmOpen && (
        <ConfirmImportDialog
          kind={kind}
          rowCount={rows.length}
          rowsWithId={withIdCount}
          orgName={needsOrg(kind) ? (orgList.data?.items.find((o) => o.id === orgId)?.name ?? '') : ''}
          loading={loading}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={() => { void runImport(); }}
        />
      )}

      {errors.length > 0 && (
        <div className="error" role="alert" style={{ marginTop: '0.75rem' }}>
          {errors.length === 1 ? errors[0] : (
            <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
              {errors.map((er, i) => <li key={i}>{er}</li>)}
            </ul>
          )}
        </div>
      )}

      {result && (
        <div data-testid="refs-import-result" style={{
          marginTop: '0.75rem', padding: '0.75rem 1rem', borderRadius: 6, color: 'var(--text)',
          background: 'var(--badge-green-bg)', border: '1px solid var(--badge-green-bd)',
        }}>
          <strong>Завантажено.</strong> Створено нових: {result.inserted}, оновлено: {result.updated}
          {result.skipped > 0 && `, пропущено: ${result.skipped}`}
          {(result.quarantined ?? 0) > 0 && `, на перевірці: ${result.quarantined}`}.
          {(result.warnings?.length ?? 0) > 0 && (
            <ul style={{ margin: '0.4rem 0 0', paddingLeft: '1.1rem', fontSize: '0.85rem' }}>
              {result.warnings!.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function ConfirmImportDialog({
  kind, rowCount, rowsWithId, orgName, loading, onCancel, onConfirm,
}: {
  kind: Kind;
  rowCount: number;
  rowsWithId: number;
  orgName: string;
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const isEmployees = kind === 'employees';
  return (
    <div role="dialog" aria-modal="true" onClick={onCancel} style={{
      position: 'fixed', inset: 0, background: 'var(--overlay)', zIndex: 50,
      display: 'flex', justifyContent: 'center', alignItems: 'center',
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'var(--bg-panel)', color: 'var(--text)', padding: '1.25rem 1.5rem',
        borderRadius: 'var(--radius)', width: 540, maxWidth: '92vw',
      }}>
        <h3 style={{ marginTop: 0 }}>Завантажити «{KIND_LABELS[kind]}»?</h3>
        <p style={{ margin: '0 0 0.5rem' }}>
          Рядків у файлі: <strong>{rowCount}</strong>
          {orgName && <> · організація: <strong>{orgName}</strong></>}
        </p>
        <div style={{
          padding: '0.75rem', borderRadius: 'var(--radius-sm)', fontSize: '0.9rem',
          background: 'var(--badge-amber-bg)', border: '1px solid var(--badge-amber-bd)',
        }}>
          {isEmployees ? (
            <>Нові працівники створюються; знайдені (за ID, табельним номером або РНОКПП)
              лише доповнюються — введене вручну не змінюється.</>
          ) : rowsWithId > 0 ? (
            <>{rowsWithId} рядків мають ID: якщо запис з таким ID уже є, він
              <strong> перезапишеться даними з файлу</strong>, а поля, яких у файлі немає,
              очистяться. Рядки без ID створюються як нові.</>
          ) : (
            <>Усі рядки створяться як нові записи. Якщо завантажите той самий файл
              ще раз — зʼявляться дублікати (для повторного завантаження додайте колонку ID).</>
          )}
          <div style={{ marginTop: '0.4rem' }}>
            Завантаження атомарне: якщо хоч один рядок помилковий, нічого не збережеться.
          </div>
        </div>
        <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <button type="button" onClick={onCancel} disabled={loading}>скасувати</button>
          <button type="button" className="primary" onClick={onConfirm} disabled={loading}>
            {loading ? 'завантажуємо…' : `завантажити ${rowCount} рядків`}
          </button>
        </div>
      </div>
    </div>
  );
}
